import { CompanyStorageDisposition } from 'shared';

import { BackendRoute } from '../config/index';
import { ApiService } from '../services/index';
import { selectedCompanyStorage } from '../storage/company/selected-company.util';
import { getS3FileOriginalName } from './get-s3file-original-name.util';
import { getUploadedFile } from './uploaded-file-cache.util';

const S3_VIRTUAL_HOST_MARK = '.s3.amazonaws.com/';

// Direct re-export from source so Babel (isolatedModules) keeps it as a value binding.
export { CompanyStorageDisposition } from 'shared';

export const isStoredS3ObjectUrl = (url: null | string | undefined): url is string =>
  Boolean(url && url.startsWith('https://') && url.includes(S3_VIRTUAL_HOST_MARK));

export const fetchPresignedCompanyStorageUrl = async (
  companyId: string,
  storageUrl: string,
  disposition: CompanyStorageDisposition = CompanyStorageDisposition.ATTACHMENT,
): Promise<string> => {
  const { url } = await ApiService.post<{ expiresIn: number; url: string }>(
    { endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'secure-storage', 'presigned-get'] },
    { body: { disposition, storageUrl } },
  );
  return url;
};

export const openSelectedCompanyStorageUrlInNewTab = async (storageUrl: string): Promise<void> => {
  const companyId = selectedCompanyStorage.getState().companyId;
  if (!companyId) return;
  return openCompanyStorageUrlInNewTab(companyId, storageUrl);
};

export const openCompanyStorageUrlInNewTab = async (
  companyId: string,
  storageUrl: string,
): Promise<void> => {
  await downloadCompanyStorageFile(companyId, storageUrl);
};

export const downloadCompanyStorageFile = async (
  companyId: string,
  storageUrl: string,
): Promise<void> => {
  const pendingUpload = getUploadedFile(storageUrl);
  const objectUrl = pendingUpload ? URL.createObjectURL(pendingUpload) : null;

  const href =
    objectUrl ??
    (isStoredS3ObjectUrl(storageUrl)
      ? await fetchPresignedCompanyStorageUrl(
          companyId,
          storageUrl,
          CompanyStorageDisposition.ATTACHMENT,
        )
      : storageUrl);

  const a = document.createElement('a');
  a.href = href;
  a.download = getS3FileOriginalName(storageUrl) || 'file';
  document.body.appendChild(a);
  a.click();
  a.remove();

  if (objectUrl) URL.revokeObjectURL(objectUrl);
};

/** Sequential download triggers (browser PDF download UX) for multiple stored objects. */
export const downloadCompanyStorageFilesSequentially = async (
  companyId: string,
  links: string[],
) => {
  for (const link of links) {
    await downloadCompanyStorageFile(companyId, link);
    await new Promise<void>((resolve) => {
      window.setTimeout(() => {
        resolve();
      }, 1000);
    });
  }
};
